import React, { useEffect, useState, useCallback, useMemo, useRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import download from 'downloadjs';
import { useIntl } from "react-intl";
import _ from 'lodash';

//Components
import { Modal, ModalBody, ModalFooter, Button } from "@gpstrackit/react-controls";
import { Icon, IconButton } from '@mui/material';
import Summary from 'Components/Summary';
import VideoPlayer from 'Components/VideoPlayer';
import {
  LinearProgress,
  Image360,
} from "@gpstrackit/react-controls"

// Actions
import {
  vidFleetVideoStartStreaming,
  vidFleetVideoStopStreaming,
  vidFleetVideoGetStreamingStatus,
  vidFleetVideoGetClipById,
  vidFleetVideoGetPictureById,
  vidFleetVideoGetStreamingStatusResponse,
  vidFleetVideoCloseModal,
  clearRedux,
  vidFleetVideoGetClipRequest,
} from 'Redux/actions';

// Styles
import './vidFleet.scss';

//Variables
let vidFleetVideoRetryTimer = null;

//Selectors
const getPictureLoading = (state) => state.vidFleetVideoRedux.loadingPicture
const getPicture = (state) => state.vidFleetVideoRedux.pictureResponse
const getVideoLoading = (state) => state.vidFleetVideoRedux.loading
const getVideo = (state) => state.vidFleetVideoRedux.response
const pictureServer = (pictureId) => {
  return createSelector(
    [getPicture, getPictureLoading],
    (pictures, loading) => {
      return [loading[pictureId], pictures[pictureId]]
    }
  )
}

const videoServer = (id) => {
  return createSelector(
    [getVideo, getVideoLoading],
    (video, loading) => {
      return [loading[id], video[id]]
    }
  )
}

const VidFleetVideoComponent = (props) => {
  const dispatch = useDispatch();
  const { messages } = useIntl();

  //Ref's
  const videoPlayerRef = useRef();

  //Local States
  const [playingVideo, setPlayingVideo] = useState(null);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [showLoading, setShowLoading] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [message, setMessage] = useState("");
  const [url, setUrl] = useState(null);
  const [lastStatus, setLastStatus] = useState(null);

  //Redux States
  const openModal = useSelector(state => state.vidFleetVideoRedux.openModal);
  const { data, detail, clipRequest, loadingClipRequest, clipRequestId } = useSelector(state => state.vidFleetVideoRedux);
  const [loadingPicture, pictureResponse] = useSelector(pictureServer(data?.pictureId), (prevProps, nextProps) => {
    return thumbnail || _.isEqual(prevProps, nextProps)
  });
  const [loading, response] = useSelector(videoServer(data?.id), (prevProps, nextProps) => {
    return url || _.isEqual(prevProps, nextProps)
  });

  useImperativeHandle(null, () => ({
    downloadVideo: downloadVideo,
    downloadPicture: downloadPicture
  }));

  const startStopLiveStreaming = useCallback((action) => {
    if (action === "start") {
      dispatch(vidFleetVideoStartStreaming(data?.id));
    } else if (action === "stop") {
      dispatch(vidFleetVideoStopStreaming(data?.id));
    }
  });

  useEffect(() => {
    return () => {
      dispatch(clearRedux('VIDFLEET_VIDEO'));
    };
  }, []);

  useEffect(() => {
    if (data?.id && data?.mode === "video") {
      if (data?.autoplay && data?.type === "streaming") {
        startStopLiveStreaming('start');
      } else if (data?.type === "playback") {
        dispatch(vidFleetVideoGetClipById(data?.id));
      }
    } else if (data?.mode === "picture") {
      setShowPlayButton(false);
    }
  }, [data?.id, data?.autoplay, data?.type, data?.mode]);

  useEffect(() => {
    if (!thumbnail && data?.thumbnailUrl) {
      setThumbnail(data?.thumbnailUrl);
    } else if (!thumbnail && data?.pictureId) {
      dispatch(vidFleetVideoGetPictureById(data?.pictureId));
    }
  }, [data?.thumbnailUrl, data?.pictureId]);

  useEffect(() => {
    if (!thumbnail && pictureResponse) {
      if (pictureResponse.error) {
        setThumbnail(null);
      } else {
        setThumbnail(pictureResponse.picUrl);
      }
    }
  }, [pictureResponse]);

  useEffect(() => {
    if (response) {
      setShowPlayButton(false);
      setPlayingVideo(false);
      if (data?.mode !== "picture")
        setShowLoading(true);
      clearTimeout(vidFleetVideoRetryTimer);

      //Handle an error with the request to VidFleet API
      if (response?.error) {
        setMessage(data?.messages ? data?.messages[response?.message || 'vidFleet_video_player_errorApi'] : "Error");
        setShowPlayButton(true);
        setPlayingVideo(false);
        setShowLoading(false);
      } else {
        //The video is a streaming|live son has multiples states
        if (data?.type === "streaming") {
          //The camera is in live streaming
          if (response?.status === "live") {
            setMessage('');
            setUrl(response?.playUrl);
            setPlayingVideo(true);
            setShowPlayButton(false);
            setShowLoading(false);
            setLastStatus(response?.status);
          }
          //We need to wait until the camera wake up
          else if (response?.status === 'waitForPublish' || response?.status === 'waitForAwake') {
            setShowLoading(true);
            setMessage(data?.messages ? data?.messages['vidFleet_video_player_wakingCamera'] : "Loading");
            setPlayingVideo(false);
            vidFleetVideoRetryTimer = setTimeout(() => {
              dispatch(vidFleetVideoGetStreamingStatus(data?.id));
            }, 4000);
            setLastStatus(response?.status);
          }
          //We need to wait until the camera stops to stream
          else if (response?.status === 'waitForStop') {
            setShowLoading(true);
            setMessage(data?.messages ? data?.messages['vidFleet_video_player_stopingCamera'] : "Loading");
            setUrl(null);
            setPlayingVideo(false);
            vidFleetVideoRetryTimer = setTimeout(() => {
              dispatch(vidFleetVideoGetStreamingStatus(data?.id));
            }, 4000);
            setLastStatus(response?.status);
          }
          //The streaming is off
          else if (response?.status === 'stopped') {
            setUrl(null);
            setShowLoading(false);
            setShowPlayButton(true);
            setPlayingVideo(false);
            setMessage('');
            //if the last status was waitForAwake or waitForPublish, we need to set the message to "Camera is offline"
            if (lastStatus === 'waitForAwake' || lastStatus === 'waitForPublish') {
              setMessage(data?.messages ? data?.messages['vidFleet_video_player_cameraOffline'] : "Loading");
              setShowLoading(false);
              setPlayingVideo(false);
              setShowPlayButton(true);
            }
            setLastStatus(response?.status);
          }
          //The camera is offline
          else if (response?.status === 'offline') {
            setMessage(data?.messages ? data?.messages['vidFleet_video_player_cameraOffline'] : "Loading");
            setShowLoading(false);
            setPlayingVideo(false);
            setShowPlayButton(true);
            setLastStatus(response?.status);
          }
        }
        //The video playback has not states
        else if (data?.type === "playback") {
          setMessage('');
          setUrl(response?.mp4Url);
          setThumbnail(response?.thumbnailUrl);

          if (data?.autoplay) {
            setPlayingVideo(true);
            setShowPlayButton(false);
          } else {
            setShowPlayButton(true);
          }

          setShowLoading(false);
        }
      }
    }
  }, [response, data?.autoplay]);

  useEffect(() => {
    if (detail && clipRequestId) {
      dispatch(vidFleetVideoGetClipRequest(clipRequestId))
    }
  }, [openModal])

  //Variables
  let isUpsidedown = false;
  if (response?.rotate === "upsidedown" || pictureResponse?.rotate === "upsidedown") {
    isUpsidedown = true;
  }

  //Preview Image
  const renderThumbnail = useMemo(() => {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        {
          data?.deviceModel === "360" && data?.dewrapImage
            ?
            <Image360
              imageUrl={thumbnail}
              id={data?.id || data?.pictureId}
              isUpsidedown={isUpsidedown}
            />
            :
            <img src={thumbnail} style={{ width: '100%', height: '100%' }} />
        }
      </div>
    )
  });

  let options = {
    autoplay: true,
    controls: true,
    url: url,
    is360: (data?.deviceModel === '360' ? true : false),
    downloadable: (data?.type === "streaming" ? false : true),
    videoType: data?.type,
    notice360Message: message ? message : data?.messages ? data?.messages['vidFleet_video_player_360Message'] : "Error",
    errorMessage: message ? message : data?.messages ? data?.messages['vidFleet_video_player_retryMessage'] : "Error",
    id: data?.id,
    bigPlayButton: false,
    loading: showLoading,
  }

  if (data?.type === "streaming") {
    options.stopCallback = (id) => {
      const targetId = id ?? data?.id;
      if (!targetId) return;
      dispatch(vidFleetVideoStopStreaming(targetId));
      videoPlayerRef?.current?.clearVideo();
    };
    options.retryCallback = (id) => {
      const targetId = id ?? data?.id;
      if (!targetId) return;

      setUrl(null);
      setPlayingVideo(false);
      videoPlayerRef?.current?.clearVideo();

      dispatch(vidFleetVideoGetStreamingStatusResponse({ [targetId]: null }));
      dispatch(vidFleetVideoStartStreaming(targetId));
    };
  }

  //functions
  const downloadVideo = useCallback(() => {
    if (url) download(url);
  });

  const downloadPicture = useCallback(() => {
    if (thumbnail) {
      //download(thumbnail);
      window.open(thumbnail, '_blank');
    }
  });

  const closeModal = () => {
    setShowLoading(false);
    dispatch(clearRedux('VIDFLEET_VIDEO'));
    dispatch(vidFleetVideoCloseModal());
    setLastStatus(null);
  }

  return (
    <Modal
      open={openModal}
      size="small"
      handleClose={() => closeModal()}
      position={detail ? "right" : ""}
      title={detail ? messages['vidFleet_clip_details'] : ""}
      loading={loadingClipRequest}
    >
      <ModalBody>
        <div style={{ padding: detail && '0 10px', height: detail && '78vh' }}>
          <div style={{ width: detail ? 450 : 465, height: 300 }}>
            <div className="vidfleet-container">
              {
                data?.mode === "picture" && <>{renderThumbnail}</>
              }
              {
                data?.mode === "video" &&
                <>
                  {
                    playingVideo && <VideoPlayer isUpsidedown={isUpsidedown} ref={videoPlayerRef} {...options} loading={true} />
                  }

                  {
                    thumbnail && !playingVideo && <>{renderThumbnail}</>
                  }
                  {
                    //showLoading && !playingVideo && <Loading style={{ marginTop: 100, marginLeft: 180 }} size={120} />
                  }

                </>
              }
              <div className="vidfleet-actions-container">
                {
                  (loading || showLoading || loadingPicture) && <LinearProgress color="primary" />
                }
                <div className="vidfleet-message">{message}</div>
                {
                  (showPlayButton && !loading && !showLoading && !loadingPicture) &&
                  <div className="vidfleet-play-button">
                    <IconButton
                      aria-label="play"
                      style={{ color: (thumbnail ? 'black' : 'white') }}
                      disabled={data?.disabled}
                      onClick={(e) => {
                        e.preventDefault();
                        if (data?.type === "streaming") {
                          startStopLiveStreaming('start');
                        } else if (data?.type === "playback") {
                          setPlayingVideo(true);
                          setShowPlayButton(false);
                        }
                      }}
                      size="large"
                    >
                      <Icon style={{ fontSize: 50, color: (data?.disabled ? 'rgba(255, 255, 255, 0.3)' : 'white') }}>
                        {(response && response && response.error) ? 'replay' : 'play_arrow'}
                      </Icon>
                    </IconButton>
                  </div>
                }
              </div>
            </div>
          </div>

          {clipRequest && clipRequestId && (
            <div style={{ marginTop: 10, paddingBottom: 20 }}>
              <Summary data={{ ...clipRequest }} colums={3} addDash timezone={data?.timezone} />
            </div>
          )}
        </div>

      </ModalBody>
      <ModalFooter>
        <div className={!detail && "vidfleet-container-footer"}>
          <Button
            className="btn-dark-blue"
            onClick={(e) => closeModal()}
          >
            {data?.messages['common_cancel']}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

export default VidFleetVideoComponent;